import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {
  CreateTaskParams,
  CreateJobParams,
  UnhydratedTask,
  OptimisticTask,
  CreateTaskMutationParams,
} from '../types/task'
import {fetchCAPI, FetchCAPIError} from '../utils/fetch-capi'
import {uuid} from '../utils/uuid'
import {agentTasksQueryKey} from './useTasks'
import {sendEvent} from '@github-ui/hydro-analytics'

/**
 * Error class for job creation failures with user-friendly messages
 */
export class CreateJobError extends Error {
  status?: number

  constructor(originalError: unknown) {
    // Extract status code
    let status: number | undefined
    if (
      originalError &&
      typeof originalError === 'object' &&
      'status' in originalError &&
      typeof originalError.status === 'number'
    ) {
      status = originalError.status
    }

    // Map status to user-friendly message
    let message: string

    switch (status) {
      case 0:
        message = 'Network error. Please check your connection and try again.'
        break

      case 400:
        message = "We couldn't start the task with the information provided. Please verify your input and try again."
        break

      case 401:
        message = "You don't have permission to create a pull request in this repository."
        break

      case 402:
        message = "You've run out of Copilot premium quota for automated changes."
        break

      case 403:
        message = "The Coding Agent isn't available for this repository or organization."
        break

      case 404:
        // Until github/sweagentd#1035 is addressed, we will add a hint to the error message for users to check
        // if the repository has at least one commit.
        message =
          "We couldn't find the repository or resource needed to create the pull request. If this is a new repository, make sure it has at least one commit."
        break

      case 409:
        message = 'A pull request already exists for these branches.'
        break

      case 422:
        message = 'A repository rule blocked automatic branch or PR creation.'
        break

      default:
        message = 'An unexpected error occurred. Please try again.'
        break
    }

    super(message, {cause: originalError})
    this.name = 'CreateJobError'
    this.status = status
  }
}

export function useCreateTask({onError}: {onError?: (error: Error, variables: CreateTaskMutationParams) => void}) {
  const queryClient = useQueryClient()

  const createTaskMutation = useMutation<string, Error, CreateTaskMutationParams>({
    mutationKey: ['createTask'],
    onMutate: async (params: CreateTaskMutationParams) => {
      // Cancel any existing refetches (prevents race conditions)
      await queryClient.cancelQueries({queryKey: agentTasksQueryKey})

      // Snapshot the previous value for potential rollback
      const previousTasks = queryClient.getQueryData<Array<UnhydratedTask | OptimisticTask>>(agentTasksQueryKey) ?? []

      // Add optimistic task to cache
      const optimisticTask = createOptimisticTask(params)
      queryClient.setQueryData(agentTasksQueryKey, [optimisticTask, ...previousTasks])

      // Return context for potential rollback
      return {previousTasks}
    },
    mutationFn: async (params: CreateTaskMutationParams) => {
      sendEvent('copilot.agent_session.job_creation_requested', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
      })
      const response = await createJob({
        ...params,
        title: params.title ?? params.problemStatement,
      })
      if (!response.ok) {
        sendEvent('copilot.agent_session.job_creation_failed', {
          event_identifiers: params.clientGeneratedId,
          event_type: params.eventType,
          nwo: params.nwo,
          status: response.status,
          error: response.statusText,
        })
        throw new Error(`Failed to create task: ${params.clientGeneratedId}`)
      }

      const jobData = await response.json()

      sendEvent('copilot.agent_session.job_creation_completed', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
        session_id: jobData.session_id,
        job_id: jobData.job_id,
      })

      return params.clientGeneratedId
    },
    onError: (error, variables) => {
      // Only remove the specific failed task, there could be many in flight at the same time
      const currentTasks = queryClient.getQueryData<Array<UnhydratedTask | OptimisticTask>>(agentTasksQueryKey) ?? []
      const tasksWithoutFailed = currentTasks.filter(task => task.clientGeneratedId !== variables.clientGeneratedId)
      queryClient.setQueryData(agentTasksQueryKey, tasksWithoutFailed)

      // Call the passed callback with returned error and original variables
      onError?.(error, variables)
    },
    onSuccess: () => {
      // Invalidate and refetch tasks query to warm up the cache
      queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
    },
  })

  const createTask = (params: CreateTaskParams, clientTaskId?: string) => {
    const clientGeneratedId = clientTaskId ?? uuid()
    createTaskMutation.mutate({...params, clientGeneratedId})
  }

  return {
    createTask,
    isCreating: createTaskMutation.isPending,
    createError: createTaskMutation.error,
  }
}

// Helper function to create job via the Jobs API
async function createJob({
  clientGeneratedId,
  nwo,
  problemStatement,
  baseRef,
  eventType,
  eventContent,
  title,
  reference,
  customAgent,
  model,
}: CreateJobParams): Promise<Response> {
  const endpoint = `/agents/swe/jobs/${nwo}`
  const referenceString = reference ? `On ${reference}, ` : ''

  try {
    return await fetchCAPI({
      path: endpoint,
      method: 'POST',
      body: {
        problem_statement: referenceString + problemStatement,
        pull_request: {
          ...(baseRef && {base_ref: `refs/heads/${baseRef}`}),
          ...(title && {title}),
        },
        event_identifiers: [clientGeneratedId],
        event_type: eventType,
        event_content: eventContent,
        custom_agent: customAgent,
        model: model?.id,
      },
    })
  } catch (error) {
    // Only wrap API errors with user-friendly messages
    // For non-API errors, throw them as-is
    if (error instanceof FetchCAPIError) {
      throw new CreateJobError(error)
    }
    throw error
  }
}

function createOptimisticTask({
  clientGeneratedId,
  nwo,
  problemStatement,
  baseRef,
  eventType,
  customAgent,
}: CreateTaskMutationParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1, // Placeholder until we get real ID from server
    resource_global_id: '', // Placeholder until we get real ID from server
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    event_identifiers: [clientGeneratedId],
    event_type: eventType,
    isOptimistic: true,
    clientGeneratedId,
    nwo,
    problemStatement,
    baseRef,
    custom_agent: customAgent,
  }
}
